<template>
  <v-row v-if="filteredAmenities.length" class="additional-features">
    <v-col cols="12">
      <h3>Additional Amenities</h3>
      <div v-if="isLoading" class="text-center pa-4">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
        <p class="mt-2">Loading amenities...</p>
      </div>
       <!-- DEBUG: Test API Button -->
      <v-btn 
        v-if="!isLoading && !filteredAmenities.length" 
        small 
        color="info"
        class="mb-2"
        @click="fetchAmenities"
      >
        Debug: Reload Amenities
      </v-btn>
      <v-row v-else>
        <v-col
          v-for="amenity in filteredAmenities"
          :key="amenity.id"
          cols="6"
          class="checkbox-column"
        >
          <v-checkbox
            :model-value="isAmenitySelected(amenity)"
            :label="amenity.name"
            dense
            class="compact-checkbox"
            @update:model-value="(checked) => toggleAmenity(amenity, checked)"
          />
        </v-col>
      </v-row>
      
      <!-- Debug Info -->
      <!-- <div v-if="selectedAmenityIds.length" class="mt-4 pa-2" style="background: #f0f0f0; border-radius: 4px;">
        <small><strong>Selected:</strong> {{ selectedAmenityIds.length }} amenities</small>
        <br>
        <small>IDs: {{ selectedAmenityIds.join(', ') }}</small>
      </div> -->
    </v-col>
  </v-row>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import { debugProps } from '@/utils/debugUtils';
import { isCategoryType, isPropertyCategory, getBaseCategoryFromKey } from "@/utils/categorySubCategorySlugUtils";

// Vuex store and route
const store = useStore();
const route = useRoute();

// Define props with default value
const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  categorySlug: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(["update:modelValue"]);

// Get amenities from store with detailed logging
const allAmenities = computed(() => {
  const amenities = store.getters["amenities/getAllAmenities"];
  console.log('🎯 CHECKBOX COMPONENT: allAmenities computed triggered:', {
    count: amenities.length,
    sampleAmenities: amenities.slice(0, 3),
    allIds: amenities.map(a => a.id).slice(0, 10)
  });
  return amenities;
});

const isLoading = computed(() => {
  const loading = store.getters["amenities/isLoading"];
  console.log('🎯 CHECKBOX COMPONENT: isLoading computed:', loading);
  return loading;
});

// Track selected amenity IDs for easier debugging
const selectedAmenityIds = computed(() => {
  if (!props.modelValue) return [];
  
  const value = Array.isArray(props.modelValue) ? props.modelValue : [];
  const ids = value.map(item => {
    if (item && typeof item === 'object' && item.id) {
      return item.id;
    } else if (typeof item === 'number') {
      return item;
    }
    return null;
  }).filter(Boolean); // Filter out null/undefined values
  
  return ids;
});

// Check if specific amenity is selected
const isAmenitySelected = (amenity) => {
  if (!amenity || !amenity.id) return false;
  const isSelected = selectedAmenityIds.value.includes(amenity.id);
  return isSelected;
};

// Toggle amenity selection
// Toggle amenity selection
const toggleAmenity = (amenity, checked) => {
  if (!amenity || typeof amenity !== 'object') {
    console.error('Invalid amenity object:', amenity);
    return;
  }

  console.log(`🔄 Toggling amenity ${amenity.id} (${amenity.name}):`, { checked });
  
  const currentValue = Array.isArray(props.modelValue) ? [...props.modelValue] : [];
  
  if (checked) {
    // Add amenity - check if it's not already added
    const exists = currentValue.some(item => {
      if (typeof item === 'object' && item && item.id) {
        return item.id === amenity.id;
      } else if (typeof item === 'number') {
        return item === amenity.id;
      }
      return false;
    });
    
    if (!exists) {
      const amenityObject = {
        id: amenity.id,
        name: amenity.name || '',
        icon: amenity.icon || null,
        is_available: true
      };
      currentValue.push(amenityObject);
      console.log('✅ Added amenity:', amenityObject);
      emit("update:modelValue", currentValue);
    }
  } else {
    // Remove amenity
    const filteredValue = currentValue.filter(item => {
      if (typeof item === 'object' && item && item.id) {
        return item.id !== amenity.id;
      } else if (typeof item === 'number') {
        return item !== amenity.id;
      }
      return true;
    });
    
    console.log('❌ Removed amenity, new count:', filteredValue.length);
    emit("update:modelValue", filteredValue);
  }
};
const subcategoryKey = computed(() => {
  // Priority 1: Use props.categorySlug if it contains the full key format
  if (props.categorySlug && props.categorySlug.includes('-')) {
    return props.categorySlug;
  }
  
  // Priority 2: Build from store/route data
  const categorySlug = store.getters["NewCategory/selectedCategory"]?.slug || 
                      route.params.categorySlug || 
                      props.categorySlug || null;
                      
  const subcategoryId = store.getters["NewCategory/selectedSubCategory"]?.id || 
                       route.params.subcategoryId || null;
  
  // Priority 3: Try to extract subcategoryId from route params
  if (!subcategoryId && route.params.subcategorySlug) {
    const subcategory = store.state.NewCategory.subcategories?.find(s => 
      s.slug === route.params.subcategorySlug
    );
    if (subcategory) {
      return `${categorySlug}-${subcategory.id}`;
    }
  }
  
  // Priority 4: Fallback to just category slug for broad matching
  return subcategoryId ? `${categorySlug}-${subcategoryId}` : categorySlug;
});

// Define amenity name mappings for categories and subcategories
const amenityNameMappings = {
  // Existing specific mappings
  "property-for-sale-7": "all", // Use all amenities
  "property-for-rent-11": "all",
  "property-for-rent-12": "all",
  "property-for-rent-13": "all",
  "property-for-rent-14": "all",
  "property-for-rent-15": "all",
  "property-for-sale-8": [
    "Shared Spa", "Shared Gym", "Covered Parking", "View of Water", 
    "View of Landmark", "Conference Room", "Available Furnished",
    "Available Networked", "Dining in Building", "Retail in Building",
  ],
  
  // Fallback mappings for category-only keys
  "property-for-sale": "all",
  "property-for-rent": "all",
  "motors": ["Covered Parking", "Security", "CCTV Security"],
};

// Enhanced filtering using utility functions and API data
const filteredAmenities = computed(() => {
  const key = subcategoryKey.value;

  console.log('🎯 CHECKBOX COMPONENT: filteredAmenities computing for key:', key);
  console.log('🎯 Available amenities count:', allAmenities.value.length);

  // Ensure amenities are only displayed for property-related categories
  if (!isPropertyCategory(key)) {
    console.warn(`⚠️ Current category/subcategory (${key}) is not property-related. No amenities will be displayed.`);
    return [];
  }

  if (!key || !allAmenities.value.length) {
    console.log('⚠️ No key or no amenities available');
    return [];
  }

  // Try exact match first
  let allowedNames = amenityNameMappings[key];

  // Fallback 1: Try base category if specific key not found
  if (!allowedNames) {
    const baseCategory = getBaseCategoryFromKey(key);
    if (baseCategory && baseCategory !== key) {
      allowedNames = amenityNameMappings[baseCategory];
      console.log(`Using fallback base category: ${baseCategory}`);
    }
  }

  // Fallback 2: Use utility function to check for property categories
  if (!allowedNames && isPropertyCategory(key)) {
    allowedNames = "all";
    console.log("Using all property amenities via utility function");
  }

  // Fallback 3: Empty array if no matches
  if (!allowedNames) {
    console.warn(`No amenity mapping found for key: ${key}`);
    return [];
  }

  // Filter amenities based on mapping
  let filtered;
  if (allowedNames === "all") {
    filtered = allAmenities.value;
  } else if (Array.isArray(allowedNames)) {
    filtered = allAmenities.value.filter((amenity) =>
      allowedNames.includes(amenity.name)
    );
  } else {
    filtered = [];
  }

  console.log('🎯 CHECKBOX COMPONENT: Filtered amenities result:', {
    key,
    totalAvailable: allAmenities.value.length,
    filteredCount: filtered.length,
    filteredIds: filtered.map(a => a.id),
    filteredNames: filtered.map(a => a.name)
  });

  return filtered;
});

// Watch for changes in key computed properties
watch(allAmenities, (newAmenities, oldAmenities) => {
  console.log('👀 WATCH: allAmenities changed:', {
    oldCount: oldAmenities?.length || 0,  // Added null check with ?. operator
    newCount: newAmenities?.length || 0,
    newSample: newAmenities?.slice?.(0, 3) || []
  });
}, { immediate: true });

watch(selectedAmenityIds, (newIds, oldIds) => {
  console.log('👀 WATCH: selectedAmenityIds changed:', {
    old: oldIds || [],
    new: newIds || [],
    count: newIds?.length || 0  // Added null check
  });
}, { immediate: true });

watch(() => props.modelValue, (newValue, oldValue) => {
  console.log('👀 WATCH: props.modelValue changed:', {
    old: oldValue,
    new: newValue,
    oldCount: Array.isArray(oldValue) ? oldValue.length : 0,
    newCount: Array.isArray(newValue) ? newValue.length : 0
  });
}, { immediate: true, deep: true });


// Add this method to test the API call
const fetchAmenities = async () => {
  try {
    console.log('📡 Manual API call to fetch amenities...');
    const result = await store.dispatch("amenities/fetchAmenities");
    console.log('✅ Manual API call completed:', {
      resultCount: result ? result.length : 'no result',
      storeCount: allAmenities.value.length
    });
  } catch (error) {
    console.error('❌ Manual API call failed:', error);
  }
};


// Enhanced debugging on mount
onMounted(async () => {
  console.log('🚀 CHECKBOX COMPONENT: onMounted started');
  console.log('🔧 Initial state:', {
    propsModelValue: props.modelValue,
    propsCategorySlug: props.categorySlug,
    storeAmenitiesCount: allAmenities.value.length,
    isCurrentlyLoading: isLoading.value
  });
  
  // Fetch amenities if not already loaded
  if (!allAmenities.value.length && !isLoading.value) {
    try {
      console.log('📡 Dispatching fetchAmenities action...');
      const result = await store.dispatch("amenities/fetchAmenities");
      console.log('✅ fetchAmenities completed:', {
        resultCount: result ? result.length : 'no result',
        storeCount: allAmenities.value.length
      });
    } catch (error) {
      console.error('❌ Failed to load amenities:', {
        error: error.message,
        stack: error.stack
      });
    }
  } else {
    console.log('ℹ️ Amenities already available or loading:', {
      count: allAmenities.value.length,
      loading: isLoading.value
    });
  }
  
  // Final state check
  console.log('🏁 CHECKBOX COMPONENT: onMounted completed, final state:', {
    amenitiesCount: allAmenities.value.length,
    filteredCount: filteredAmenities.value.length,
    loading: isLoading.value,
    subcategoryKey: subcategoryKey.value
  });
  
  debugProps('CheckBoxFeatures', props);
});
</script>

<style scoped>
.additional-features {
  margin-top: 8px;
}

h3 {
  font-size: 1.1rem;
  font-weight: bold;
  margin-bottom: 8px;
}

.checkbox-column {
  padding: 0.9px;
}

.compact-checkbox {
  margin-bottom: 0.1px;
  font-size: 0.9rem;
}
</style>